// Homepage with pain-first messaging and clear value proposition
import { Button } from "@/components/ui/button";
import { UTMLink } from '@/components/custom-ui/utm-link';
import { useState } from "react";
import { CheckCircle, Play, Shield, ChevronDown } from "lucide-react";

import { motion, AnimatePresence } from "framer-motion";
import scanDnaImage from "@/assets/images/scan_dna.png";
import convergencePhoto from "@/assets/images/convergence.jpg";
import convergencesImage from "@/assets/images/convergences.png";
import reviewEmailImage from "@/assets/images/small_review_email.png";
import newsItemsImage from "@/assets/images/lots_of_news_items.jpg";
import brandGeniusImage from "@/assets/images/brand_genius.png";
import instagramPostImage from "@/assets/images/instagram_post.png";
import { GradientText } from "@/components/ui/gradient-text";
import VideoModal from '@/components/custom-ui/video-modal';
import { Logos3 } from "@/components/ui/logos3";
import BrandDnaScan from '@/components/custom-ui/BrandDnaScan';

// Use UTMLink for preserving UTM parameters
const Link = UTMLink;

// FAQ data for the basic FAQ section
const basicFaqs = [
  {
    question: "Will it sound like my brand?",
    answer: "Yes. Our AI analyzes your existing content, website, and brand guidelines to match your exact voice and tone, and you can use our AI to make it even better. You can adjust the style anytime."
  },
  {
    question: "Do I need to appear on camera?",
    answer: "No. Choose from text overlays, product shots, stock footage, or AI-generated visuals. If you want to be on camera, we provide short, easy scripts."
  },
  {
    question: "Can I edit the content?", 
    answer: "Absolutely. Edit anything directly in the platform. Change copy, swap images, adjust colors - then regenerate all platform versions with one click."
  },
  {
    question: "Which platforms do you support?",
    answer: "Currently: Facebook, Instagram, LinkedIn, and blogs. Coming soon: TikTok, YouTube, Twitter/X, Pinterest. Each with platform-specific formatting."
  },
  {
    question: "Is my data secure?",
    answer: "Yes. SOC2 Type II compliant, GDPR compliant, and your content is never shared or used to train AI for others."
  }
];


export default function Home() {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);


    return (<>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative w-full box-border px-3 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          
          {/* Hero Section with Pain-First Messaging */}
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm mt-8 mb-8">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
            <div className="pt-8 sm:pt-16 pb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="relative w-full max-w-4xl mx-auto text-center px-4"
              >
                {/* Main Headline */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 tracking-tight leading-[1.1] text-gray-900">
                  AI Content That's Finally <GradientText from="from-blue-600" to="to-indigo-600" className="font-bold">Relevant</GradientText> and <GradientText from="from-purple-600" to="to-pink-600" className="font-bold">Authentic</GradientText>.
                </h1>
                
                {/* Subheading */}
                <h2 className="text-xl sm:text-2xl lg:text-3xl mb-8 text-gray-800 max-w-4xl mx-auto leading-relaxed font-semibold">
                  Rainfactory is the only AI that finds the latest trending topics that are relevant to your customers <span className="text-blue-600">(convergences)</span> and automatically creates content in your brand's unique voice <span className="text-purple-600">(your brand's DNA)</span>.
                </h2>

                {/* Subtitle */}
                <p className="text-lg sm:text-xl lg:text-2xl mb-8 text-gray-900 max-w-4xl mx-auto leading-relaxed font-bold">
                  This is how you get <GradientText from="from-green-600" to="to-emerald-600" className="font-bold">40+ hours back</GradientText> and get <GradientText from="from-blue-600" to="to-indigo-600" className="font-bold">better results</GradientText>.
                </p>
                
                {/* Trust Indicator */}
                <div className="mb-8">
                  <p className="text-sm text-gray-600 mb-6">
                    Join 500+ businesses creating content that's finally relevant and authentic
                  </p>
                </div>
                
                {/* CTAs */}
                <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                  <Link to="/login?flow=signup" className="w-full sm:w-auto">
                    <Button 
                      size="lg"
                      className="w-full sm:w-auto h-14 px-8 text-lg font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-0"
                    >
                      Start free 14-day trial
                    </Button>
                  </Link>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full sm:w-auto h-14 px-8 text-lg transition-transform duration-200 hover:scale-105"
                    onClick={() => setIsVideoModalOpen(true)}
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Watch 2-min demo
                  </Button>
                </div>
                
                {/* Price Anchor */}
                <p className="text-sm text-gray-600">
                  Starting at $29/mo (less than 3 hours of agency time)
                </p>

                {/* Brand DNA Scan - Simplified Hero Version */}
                <div className="mt-12 max-w-2xl mx-auto">
                  <p className="text-sm text-gray-600 mb-4 text-center font-semibold uppercase tracking-wide">
                    See it in action - Scan your brand DNA now
                  </p>
                  <BrandDnaScan
                    className="mb-0"
                    showTitle={false}
                    showDescription={false}
                  />
                </div>
              </motion.div>
            </div>
          </div>
          
          {/* Before/After Time Comparison */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
                Convergences in Action
              </h2>
              <p className="text-xl text-gray-600">
                <span className="text-blue-600 font-semibold">Live trends</span> + <span className="text-purple-600 font-semibold">Your brand voice</span> = <b>Convergences:</b> Content that converts in just 10 minutes per week
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Current Week */}
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 sm:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-red-700 mb-2">Your Current Week</h3>
                  <div className="text-4xl font-bold text-red-600">40 hours 35 minutes</div>
                </div>
                
                <div className="space-y-3">
                  {[
                    { task: "Trend research", time: "45 min" },
                    { task: "Content ideation", time: "20 min" },
                    { task: "Script writing", time: "60 min" },
                    { task: "Video creation", time: "60 min" },
                    { task: "Editing & formatting", time: "90 min" },
                    { task: "Platform optimization", time: "30 min" },
                    { task: "Thumbnail design", time: "30 min" },
                    { task: "Captions & hashtags", time: "20 min" },
                    { task: "Copywriting variants", time: "35 min" },
                    { task: "Scheduling & posting", time: "20 min" },
                    { task: "Community management", time: "30 min" },
                    { task: "Analytics review", time: "45 min" },
                    { task: "Planning next week", time: "30 min" }
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="flex justify-between items-center py-2 border-b border-red-200/50 last:border-b-0"
                    >
                      <span className="text-gray-700">{item.task}</span>
                      <span className="font-semibold text-red-600">{item.time}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* With Rainfactory */}
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 sm:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-green-700 mb-2">With Rainfactory</h3>
                  <div className="text-4xl font-bold text-green-600">10 minutes</div>
                </div>
                
                <div className="space-y-3">
                  {[
                    { task: "Review & approve created contents", time: "5 min" },
                    { task: "One-click publish", time: "2 min" }
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2 }}
                      className="flex justify-between items-center py-4 border-b border-green-200/50 last:border-b-0"
                    >
                      <span className="text-gray-700 font-medium">{item.task}</span>
                      <span className="font-bold text-green-600">{item.time}</span>
                    </motion.div>
                  ))}
                </div>
                
                <div className="mt-8 text-center">
                  <div className="bg-white/70 rounded-lg p-4">
                    <p className="text-lg font-semibold text-green-700">
                      💰 Save 40+ hours every week
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      That's over 2,000 hours per year back in your life
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* The Convergence Proof Section - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
                From Live Trend to On-Brand Post in Seconds
              </h2>
              <p className="text-xl text-gray-600">
                This is how Convergences work—live trends meet your brand DNA
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              {/* Desktop: Horizontal Layout with Math Symbols */}
              <div className="hidden lg:flex items-center justify-center gap-6">
                {/* Box 1: The Trend */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex-1 max-w-xs"
                >
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border-2 border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300 p-6 h-full">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center text-sm">1</div>
                      <span className="text-xs font-bold text-orange-400 uppercase tracking-wide">Live Trend</span>
                    </div>
                    <p className="text-sm text-slate-200 leading-relaxed mb-6">
                      Rainfactory continuously watches the zeitgeist, surfacing high-confidence trending opportunities connected to your audience.
                    </p>
                    <div className="bg-slate-900/50 rounded-xl mb-6 border border-slate-700 overflow-hidden">
                      <img
                        src={convergencePhoto}
                        alt="Trending convergence visual showing Hopecore wave analysis"
                        className="w-full h-52 object-cover"
                      />
                    </div>
                    <div className="text-xs text-slate-400 font-medium mt-4">
                      Updates: <span className="text-orange-400">Every hour</span>
                    </div>
                  </div>
                </motion.div>

                {/* Plus Symbol */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="text-5xl font-bold text-gray-300"
                >
                  +
                </motion.div>

                {/* Box 2: Your DNA */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.25, duration: 0.6 }}
                  className="flex-1 max-w-xs"
                >
                  <div className="bg-gradient-to-br from-blue-700 via-indigo-600 to-blue-500 rounded-2xl border-2 border-blue-500/80 shadow-lg hover:shadow-xl transition-all duration-300 p-6 h-full">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 rounded-full bg-blue-200 text-blue-900 font-bold flex items-center justify-center text-sm">2</div>
                      <span className="text-xs font-bold text-blue-100 uppercase tracking-wide">Your Brand's DNA</span>
                    </div>
                    <p className="text-sm text-slate-100 leading-relaxed mb-6">
                      Brand Genius captures your voice, tone, and preferences automatically, making sure every output sounds unmistakably like you.
                    </p>
                    <div className="bg-white/90 rounded-xl mb-6 border border-blue-200/70 overflow-hidden">
                      <img
                        src={brandGeniusImage}
                        alt="Brand Genius analysis dashboard showing completion progress"
                        className="w-full h-52 object-cover"
                      />
                    </div>
                    <div className="text-xs text-blue-100 font-medium mt-4">
                      Learned in: <span className="text-blue-200">30 seconds</span>
                    </div>
                  </div>
                </motion.div>

                {/* Equals Symbol */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.35, duration: 0.4 }}
                  className="text-5xl font-bold text-gray-300"
                >
                  =
                </motion.div>

                {/* Box 3: The Result */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="flex-1 max-w-xs"
                >
                  <div className="bg-gradient-to-br from-white to-green-50/30 rounded-2xl border-2 border-green-200 shadow-xl hover:shadow-2xl transition-all duration-300 p-6 h-full relative overflow-hidden">
                    {/* Magic badge */}
                    <div className="absolute top-3 right-3 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      ✨ Perfect Match
                    </div>

                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 rounded-full bg-green-500 text-white font-bold flex items-center justify-center text-sm">3</div>
                      <span className="text-xs font-bold text-green-600 uppercase tracking-wide">Perfect Post</span>
                    </div>
                    <p className="text-sm text-gray-800 leading-relaxed mb-6">
                      Final assets land instantly, ready for approval and publishing—aligned with the current trend and your brand personality.
                    </p>
                    <div className="rounded-xl overflow-hidden border border-green-200 mb-6">
                      <img
                        src={instagramPostImage}
                        alt="Example Instagram post generated by Rainfactory"
                        className="w-full h-52 object-cover"
                      />
                    </div>
                    <div className="text-xs text-gray-600 font-medium mt-4">
                      Generated in: <span className="text-green-600">3 seconds</span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Mobile/Tablet: Vertical Stack */}
              <div className="lg:hidden space-y-6">
                {/* Box 1: The Trend */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                >
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border-2 border-slate-700 shadow-lg p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center text-sm">1</div>
                      <span className="text-xs font-bold text-orange-400 uppercase tracking-wide">Live Trend</span>
                    </div>
                    <p className="text-sm text-slate-200 leading-relaxed mb-6">
                      Rainfactory continuously watches the zeitgeist, surfacing high-confidence trending opportunities connected to your audience.
                    </p>
                    <div className="bg-slate-900/50 rounded-xl mb-6 border border-slate-700 overflow-hidden">
                      <img
                        src={convergencePhoto}
                        alt="Trending convergence visual showing Hopecore wave analysis"
                        className="w-full h-52 object-cover"
                      />
                    </div>
                    <div className="text-xs text-slate-400 font-medium mt-4">
                      Updates: <span className="text-orange-400">Every hour</span>
                    </div>
                  </div>
                </motion.div>

                {/* Plus Symbol */}
                <div className="text-center">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                    className="text-4xl font-bold text-gray-300"
                  >
                    +
                  </motion.div>
                </div>

                {/* Box 2: Your DNA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.25, duration: 0.6 }}
                >
                  <div className="bg-gradient-to-br from-blue-700 via-indigo-600 to-blue-500 rounded-2xl border-2 border-blue-500/80 shadow-lg p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 rounded-full bg-blue-200 text-blue-900 font-bold flex items-center justify-center text-sm">2</div>
                      <span className="text-xs font-bold text-blue-100 uppercase tracking-wide">Your DNA</span>
                    </div>
                    <p className="text-sm text-slate-100 leading-relaxed mb-6">
                      Brand Genius captures your voice, tone, and preferences automatically, making sure every output sounds unmistakably like you.
                    </p>
                    <div className="bg-white/90 rounded-xl mb-6 border border-blue-200/70 overflow-hidden">
                      <img
                        src={brandGeniusImage}
                        alt="Brand Genius analysis dashboard showing completion progress"
                        className="w-full h-52 object-cover"
                      />
                    </div>
                    <div className="text-xs text-blue-100 font-medium mt-4">
                      Learned in: <span className="text-blue-200">30 seconds</span>
                    </div>
                  </div>
                </motion.div>

                {/* Equals Symbol */}
                <div className="text-center">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.35, duration: 0.4 }}
                    className="text-4xl font-bold text-gray-300"
                  >
                    =
                  </motion.div>
                </div>

                {/* Box 3: The Result */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                >
                  <div className="bg-gradient-to-br from-white to-green-50/30 rounded-2xl border-2 border-green-200 shadow-xl p-6 relative overflow-hidden">
                    {/* Magic badge */}
                    <div className="absolute top-3 right-3 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      ✨ Perfect Match
                    </div>

                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 rounded-full bg-green-500 text-white font-bold flex items-center justify-center text-sm">3</div>
                      <span className="text-xs font-bold text-green-600 uppercase tracking-wide">Perfect Post</span>
                    </div>
                    <p className="text-sm text-gray-800 leading-relaxed mb-6">
                      Final assets land instantly, ready for approval and publishing—aligned with the current trend and your brand personality.
                    </p>
                    <div className="rounded-xl overflow-hidden border border-green-200 mb-6">
                      <img
                        src={instagramPostImage}
                        alt="Example Instagram post generated by Rainfactory"
                        className="w-full h-52 object-cover"
                      />
                    </div>
                    <div className="text-xs text-gray-600 font-medium mt-4">
                      Generated in: <span className="text-green-600">3 seconds</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Website Analysis Card - Brand DNA Scan */}
          <BrandDnaScan />
          
          {/* How It Works - 3 Steps */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
                How It Works
              </h2>
              <p className="text-xl text-gray-600">
                The convergence of your DNA and the zeitgeist in 4 simple steps
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 relative">
                {/* Connection lines - 2x2 grid style */}
                <div className="hidden md:block absolute top-32 left-1/4 right-3/4 h-0.5 bg-gradient-to-r from-blue-300 to-amber-300" />

                {/* Step 1: Brand DNA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border-2 border-blue-200 shadow-lg p-8 hover:shadow-xl transition-all duration-300 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center text-lg">1</div>
                      <h3 className="text-xl font-bold text-gray-900">We Learn Your Brand DNA</h3>
                    </div>
                    <div className="w-full max-w-xs mx-auto mb-4 rounded-lg overflow-hidden shadow-md">
                      <img
                        src={scanDnaImage}
                        alt="Brand DNA Scan"
                        className="w-full h-auto object-cover rounded-lg"
                      />
                    </div>
                    <p className="font-semibold text-gray-900 mb-2">Start by scanning your website in 30 seconds</p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Our AI instantly teaches itself your unique voice, tone, value proposition, and target audience
                    </p>
                    <div className="mt-4 font-semibold text-blue-600">Time: 30 seconds</div>
                  </div>
                </motion.div>

                {/* Step 2: Trending Topics */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-amber-50 to-orange-100/50 rounded-2xl border-2 border-amber-200 shadow-lg p-8 hover:shadow-xl transition-all duration-300 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-amber-600 text-white font-bold flex items-center justify-center text-lg">2</div>
                      <h3 className="text-xl font-bold text-gray-900">We Find What's Trending</h3>
                    </div>
                    <div className="w-full max-w-xs mx-auto mb-4 rounded-lg overflow-hidden shadow-md">
                      <img
                        src={newsItemsImage}
                        alt="Trending Topics Research"
                        className="w-full h-auto object-cover rounded-lg"
                      />
                    </div>
                    <p className="font-semibold text-gray-900 mb-2">Our AI constantly researches live trends, news, and viral topics in your specific market</p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We find the relevant conversations your audience is already having right now
                    </p>
                    <div className="mt-4 font-semibold text-amber-600">Time: Continuous</div>
                  </div>
                </motion.div>

                {/* Step 3: The Convergence (EMPHASIZED) */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50 rounded-2xl border-4 border-purple-300 shadow-xl p-8 hover:shadow-2xl transition-all duration-300 h-full relative overflow-hidden">
                    {/* Magic badge */}
                    <div className="absolute top-4 right-4 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      The Magic ✨
                    </div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white font-bold flex items-center justify-center text-lg">3</div>
                      <h3 className="text-xl font-bold text-gray-900">We Create the Convergences</h3>
                    </div>
                    <div className="w-full max-w-xs mx-auto mb-4 rounded-lg overflow-hidden shadow-md">
                      <img
                        src={convergencesImage}
                        alt="Convergence - Brand DNA + Trends"
                        className="w-full h-auto object-cover rounded-lg"
                      />
                    </div>
                    <p className="font-semibold text-gray-900 mb-2">Rainfactory's AI combines what's trending with your unique voice</p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      The magic happens here—we transform generic trends into relevant, high-engagement posts that sound authentically like your brand
                    </p>
                    <div className="mt-4 font-semibold text-purple-600">Time: Automated</div>
                  </div>
                </motion.div>

                {/* Step 4: Review & Approve */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-green-50 to-emerald-100/50 rounded-2xl border-2 border-green-200 shadow-lg p-8 hover:shadow-xl transition-all duration-300 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-green-600 text-white font-bold flex items-center justify-center text-lg">4</div>
                      <h3 className="text-xl font-bold text-gray-900">You Review in 10 Minutes</h3>
                    </div>
                    <div className="w-full max-w-xs mx-auto mb-4 rounded-lg overflow-hidden shadow-md">
                      <img
                        src={reviewEmailImage}
                        alt="Review Campaign Email"
                        className="w-full h-auto object-cover rounded-lg"
                      />
                    </div>
                    <p className="font-semibold text-gray-900 mb-2">Get content every day</p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      You just review and approve. That's it. No creation, no research, no formatting.
                    </p>
                    <div className="mt-4 font-semibold text-green-600">Time: 10 minutes</div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
          
          {/* Primary CTA Section with Trust Indicators */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-8 sm:p-12 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">
                Ready to get 40 hours back every week?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Join 500+ businesses already saving time and growing with Rainfactory
              </p>
              
              {/* Trust badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">500+</div>
                  <div className="text-sm text-gray-600">businesses automated</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">2.3M+</div>
                  <div className="text-sm text-gray-600">pieces of content created</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">42M+</div>
                  <div className="text-sm text-gray-600">hours saved</div>
                </div>
                <div className="text-center">
                  <Shield className="h-8 w-8 text-gray-600 mx-auto mb-1" />
                  <div className="text-sm text-gray-600">SOC2 Compliant</div>
                </div>
              </div>
              
              {/* Risk Reversal */}
              <div className="bg-white/70 rounded-xl p-6 mb-8 max-w-2xl mx-auto">
                <h3 className="font-semibold text-gray-900 mb-4">Zero risk to get started</h3>
                <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    14-day free trial
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    Cancel anytime with one click
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    30-day money-back guarantee
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    Keep all content you've created
                  </div>
                </div>
              </div>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/login?flow=signup" className="w-full sm:w-auto">
                  <Button 
                    size="lg"
                    className="w-full sm:w-auto h-14 px-8 text-lg font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-0"
                  >
                    Start my free trial
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto h-14 px-8 text-lg transition-transform duration-200 hover:scale-105"
                  onClick={() => setIsVideoModalOpen(true)}
                >
                  Schedule a demo
                </Button>
              </div>
            </div>
          </motion.div>
          
          {/* Basic FAQ Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know to get started
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-4">
              {basicFaqs.map((faq, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm"
                >
                  <button
                    className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200 rounded-xl"
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    <ChevronDown 
                      className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                        openFaqIndex === index ? 'transform rotate-180' : ''
                      }`} 
                    />
                  </button>
                  <AnimatePresence>
                    {openFaqIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-4">
                          <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-gray-600">
                Still have questions? <Link to="/contact" className="text-blue-600 hover:text-blue-700 underline">Contact our support team</Link>
              </p>
            </div>
          </motion.div>
              

          


          {/* Logos */}
          <Logos3 />

          {/* Testimonials Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">
                What Our Customers Say
              </h2>
              <p className="text-xl text-gray-600">
                Real results from real businesses
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {/* Testimonial 1 */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I went from spending 8 hours every Monday creating content to just 10 minutes reviewing what Rainfactory created. It's like having a marketing team that never sleeps."
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-semibold text-gray-900">Sarah Chen</p>
                  <p className="text-sm text-gray-600">Founder, TechFlow Solutions</p>
                </div>
              </motion.div>

              {/* Testimonial 2 */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Our engagement rates doubled in the first month. The content actually sounds like us, and we're posting consistently across all platforms without the stress."
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-semibold text-gray-900">Marcus Rodriguez</p>
                  <p className="text-sm text-gray-600">Marketing Director, GreenLeaf Organics</p>
                </div>
              </motion.div>

              {/* Testimonial 3 */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Best $29 I spend every month. I've reclaimed 40 hours per week and our content quality is actually better than when I was doing it manually."
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-semibold text-gray-900">Jessica Park</p>
                  <p className="text-sm text-gray-600">CEO, Artisan Coffee Co.</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Video Modal */}
      <VideoModal 
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoUrl="https://www.youtube.com/embed/dQw4w9WgXcQ" // Replace with your actual demo video URL
      />
  </>);
}